<script setup lang="ts">
import AuthPasswordStrength from '@/components/AuthPasswordStrength/AuthPasswordStrength.vue';
import AuthTestimonialCarousel from '@/components/AuthTestimonialCarousel/AuthTestimonialCarousel.vue';
import { useI18n } from '@/i18n';
import { ssoErrorMessageKey } from '@/lib/auth-api-error-codes';
import { isInviteRole } from '@/lib/roles';
import { createSignupSchema, type SignupFormValues } from '@/schemas/signup';
import { useAuthStore } from '@/stores/auth';
import { useAuthFlow, type SsoSuccessPayload } from '@/use/authFlow';
import { Alert, Button, Form, Heading, Input, LayoutGap, Loader, Prose } from '@dabble/quillui';
import AuthLayout from '@/components/AuthLayout/AuthLayout.vue';
import AuthInviteBanner from '@/components/AuthInviteBanner/AuthInviteBanner.vue';
import AuthTemplateBanner from '@/components/AuthTemplateBanner/AuthTemplateBanner.vue';
import AuthSsoButtons from '@/components/AuthSsoButtons/AuthSsoButtons.vue';
import { toTypedSchema } from '@vee-validate/yup';
import { useForm, useIsFormDirty } from 'vee-validate';
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

const route = useRoute();
const { t, te } = useI18n();

function translateFieldError(raw: string | undefined) {
  if (!raw) return '';
  return te(raw) ? t(raw) : raw;
}
const authStore = useAuthStore();
const { resolveSignupContext, completeAuth } = useAuthFlow();

const error = ref<string | undefined>(undefined);
const isSubmitting = ref(false);

const rawSignupSchema = computed(() => createSignupSchema((key: string) => t(key)));
const validationSchema = computed(() => toTypedSchema(rawSignupSchema.value));

const { handleSubmit, defineField, values } = useForm<SignupFormValues>({
  validationSchema,
  initialValues: {
    name: '',
    email: '',
    password: '',
  },
});

/** `useIsFormValid()` can stay true before vee runs validation; mirror the Yup schema for the button + Quill Form. */
const isValid = computed(() =>
  rawSignupSchema.value.isValidSync({
    name: values.name ?? '',
    email: values.email ?? '',
    password: values.password ?? '',
  }),
);

const isDirty = useIsFormDirty();

const fieldOpts = {
  validateOnBlur: true,
  validateOnChange: true,
  validateOnModelUpdate: true,
  props: (state: { errors: string[] }) => ({
    error: state.errors.length > 0,
    errorMessage: translateFieldError(state.errors[0]),
  }),
};

const [name, nameField] = defineField('name', fieldOpts);
const [email, emailField] = defineField('email', fieldOpts);
const [password, passwordField] = defineField('password', fieldOpts);

function readQueryOrSession(key: string): string {
  const q = route.query[key];
  if (q !== undefined && q !== null && q !== '') {
    return String(Array.isArray(q) ? q[0] : q).trim();
  }
  const s = sessionStorage.getItem(key);
  return s ? String(s).trim() : '';
}

/** Invited collaborator (beta reader / editor): show "free account" copy, not the trial pitch. */
const isInvited = computed(() => isInviteRole(readQueryOrSession('role')));

/** Same signup intent for the SSO buttons as the password form resolves at submit. */
const ssoSignUpOptions = computed(() => resolveSignupContext().signUpOptions);

async function createAccount(values: SignupFormValues) {
  const context = resolveSignupContext();

  const response = await authStore.signUp(values.name, values.email, values.password, context.signUpOptions);

  if (response && response.user) {
    // Post-signup sequence (conversion tracking, billing sync + referral
    // coupon on trials, onboarding) lives in the shared auth flow.
    await completeAuth(response.user, true, context);
  }
}

const onSubmit = handleSubmit(async (values) => {
  error.value = undefined;
  isSubmitting.value = true;
  try {
    await createAccount(values);
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : String(e);
    error.value = te(message) ? t(message) : message;
  } finally {
    isSubmitting.value = false;
  }
});

/** Post-auth routing is decided by `isNewUser`, not by which view hosted the button. */
async function onSsoSuccess(payload: SsoSuccessPayload) {
  error.value = undefined;
  isSubmitting.value = true;
  try {
    await completeAuth(payload.user, payload.isNewUser);
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : String(e);
    error.value = te(message) ? t(message) : message;
  } finally {
    isSubmitting.value = false;
  }
}

function onSsoError(code: string) {
  error.value = ssoErrorMessageKey(code);
}
</script>

<template>
  <AuthLayout :show-logo="false">
    <template #header>
      <div class="signup__logo-mobile">
        <img src="/images/dabble-logo-inverted.svg" alt="Dabble" />
      </div>
    </template>

    <template #aside>
      <div class="signup__intro">
        <div class="signup__logo">
          <img src="/images/dabble-logo-inverted.svg" alt="Dabble" />
        </div>
        <Heading level="title" family="secondary" class="signup__blurb" :margin="false">{{
          t('create_account_blurb')
        }}</Heading>
        <AuthTestimonialCarousel />
      </div>
    </template>

    <AuthInviteBanner />
    <AuthTemplateBanner />

    <Form v-model:dirty="isDirty" :valid="isValid" :actions="false" class="signup__form" @submit="onSubmit">
      <LayoutGap direction="column" size="medium" align="center" class="signup__form-contents">
        <LayoutGap direction="column" size="tiny" align="center">
          <Heading :level="3" :margin="false">{{ t('create_account') }}</Heading>
          <Prose v-if="!isInvited">{{ t('create_account_intro') }}</Prose>
        </LayoutGap>
        <div role="alert" aria-live="assertive" aria-atomic="true">
          <Alert v-if="error" variant="warning">{{ t(error) }}</Alert>
        </div>
        <!-- v-show, not v-if: a remount re-runs GIS initialize/renderButton (Google
             documents initialize as call-once) and re-creates the button iframe with
             a visible flash after every submit cycle — including failed ones. -->
        <div v-show="!isSubmitting" class="signup__sso">
          <AuthSsoButtons
            mode="signup"
            :sign-up-options="ssoSignUpOptions"
            @success="onSsoSuccess"
            @error="onSsoError"
          />
        </div>
        <div v-if="isSubmitting" class="signup__loader">
          <Loader />
        </div>
        <LayoutGap v-else direction="column" align="center" size="small" class="signup__form-stack">
          <Input
            id="name"
            v-model="name"
            class="auth-field"
            v-bind="nameField"
            :label="t('name')"
            type="text"
            required
            size="large"
            :placeholder="t('signup_placeholder_name')"
          />
          <Input
            id="email"
            v-model="email"
            class="auth-field"
            v-bind="emailField"
            :label="t('email')"
            type="text"
            required
            size="large"
            :placeholder="t('signup_placeholder_email')"
          />
          <AuthPasswordStrength
            v-model="password"
            v-bind="passwordField"
            :placeholder="t('signup_placeholder_password')"
            show-requirements
            :allow-empty="false"
            class="signup__password auth-field"
          />
          <Button
            class="signup__submit"
            fill-width
            size="large"
            :disabled="authStore.signingUp || !isValid"
            type="submit"
          >
            {{ t('get_started') }}
          </Button>
        </LayoutGap>
      </LayoutGap>
    </Form>

    <template #footer>
      <p class="signup__login-link">
        {{ t('have_account') }}
        <router-link to="/auth/login">{{ t('login') }}</router-link>
      </p>
    </template>
  </AuthLayout>
</template>

<style lang="scss" scoped>
@use '@/styles/auth-form.scss';

.signup {
  &__intro {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: space('large');
    width: 432px;
    max-width: 100%;
  }

  &__logo {
    align-self: flex-start;
    max-width: 100%;

    img {
      display: block;
      max-width: 100%;
      max-height: 112px;
      width: auto;
      height: auto;
      object-fit: contain;
      filter: drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.1));
    }
  }

  &__blurb {
    align-self: flex-start;
    text-align: left;
    color: theme('color-text-primary-inverted');
    filter: drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.2));
  }

  &__form {
    width: 100%;
  }

  &__form-stack,
  &__password,
  &__sso {
    width: 100%;
  }

  &__loader {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    padding: space('huge') 0;
  }

  &__login-link {
    margin: 0;
  }

  &__submit {
    margin-top: space('medium');
  }

  &__logo-mobile img {
    display: block;
    height: 64px;
    width: auto;
    max-width: 100%;
    object-fit: contain;
    filter: drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.1));
  }
}

// Desktop: the logo lives in the aside column, so hide the top header logo.
:deep(.auth-layout__header) {
  display: none;
}

// Mobile + iPad portrait: drop the byline + testimonial column and show the
// logo at the top instead — below 'laptop' the columns can't sit side by side.
@include media-query-below('laptop') {
  :deep(.auth-layout__aside) {
    display: none;
  }

  :deep(.auth-layout__header) {
    display: flex;
  }
}
</style>
